import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ThemeToggle } from '../components/ThemeToggle';
import { LanguageSelector } from '../components/LanguageSelector';
import { useI18n } from '../contexts/I18nContext';
import { Modal } from '../components/Modal';
import { STRIPE_COUNTRIES } from '../lib/countries';
import { Mail, Lock, User, AlertCircle, CheckCircle, Globe, AlertTriangle, Copy } from 'lucide-react';

/**
 * Detects if the current browser is an in-app browser on iOS.
 * Android in-app browsers (Chrome Custom Tabs) usually support Google Sign-in,
 * but iOS WebViews (LinkedIn, Instagram) trigger Google's 403 disallowed_useragent error.
 */
const isIosInAppBrowser = () => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  
  // Check if it is a known in-app browser
  const rules = [
    'LinkedInApp',
    'Instagram',
    'FBAN',   // Facebook
    'FBAV',   // Facebook
    'Line',
    'Musical_ly', // TikTok
  ];
  const isInternal = new RegExp('(' + rules.join('|') + ')').test(ua);

  // Check if it is iOS (iPhone, iPad, iPod)
  const isIOS = /iPad|iPhone|iPod/.test(ua) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Only return true if BOTH conditions are met
  return isInternal && isIOS;
};

/**
 * Parses OAuth/signup errors and returns user-friendly messages.
 */
function getReadableErrorMessage(error: any): string {
  const message = error?.message || error?.error_description || String(error);
  const lowerMessage = message.toLowerCase();

  if (
    lowerMessage.includes('user already registered') ||
    lowerMessage.includes('email already registered') ||
    lowerMessage.includes('account already exists') ||
    lowerMessage.includes('identity already exists') ||
    lowerMessage.includes('email address already') ||
    lowerMessage.includes('duplicate key') ||
    lowerMessage.includes('already been registered')
  ) {
    return 'An account with this email address already exists. Please sign in instead.';
  }

  if (lowerMessage.includes('oauth') && lowerMessage.includes('error')) {
    return 'Unable to connect to Google. Please try again or use email signup.';
  }

  if (lowerMessage.includes('rate limit') || lowerMessage.includes('too many requests')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }

  if (lowerMessage.includes('invalid') && lowerMessage.includes('credentials')) {
    return 'Invalid credentials. Please check your email and password.';
  }

  if (lowerMessage.includes('valid email')) {
    return 'Please enter a valid email address.';
  }

  if (lowerMessage.includes('password') && (lowerMessage.includes('short') || lowerMessage.includes('weak') || lowerMessage.includes('least'))) {
    return 'Password must be at least 6 characters long.';
  }

  return message;
}

// Google icon SVG component
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24">
      <path
        fill="currentColor"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="currentColor"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="currentColor"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="currentColor"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

export function RegisterPage() {
  const { tt } = useI18n();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [country, setCountry] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  // State for in-app browser warning
  const [showInAppWarning, setShowInAppWarning] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const { signUp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  // Set page title and noindex meta tag
  useEffect(() => {
    document.title = tt('Create an account') + ' - Llambada';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.title = 'Llambada';
      document.head.removeChild(meta);
    };
  }, [tt]);

  const clearError = () => setError('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setLoading(true);

    if (password.length < 6) {
      setError(tt('Password must be at least 6 characters'));
      setLoading(false);
      return;
    }

    if (!country) {
      setError(tt('Please select your country'));
      setLoading(false);
      return;
    }

    const { error } = await signUp(email, password, displayName, country);

    if (error) {
      setError(getReadableErrorMessage(error));
      setLoading(false);
    } else {
      setSuccess(true);
      setLoading(false);
      // Preserve returnTo param when redirecting to login
      const loginUrl = returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login';
      setTimeout(() => navigate(loginUrl), 3000);
    }
  };

  const handleGoogleSignup = async () => {
    // Check if we are in an iOS environment that Google blocks
    if (isIosInAppBrowser()) {
      setShowInAppWarning(true);
      return;
    }

    setGoogleLoading(true);
    clearError();
    
    const { error } = await signInWithGoogle(returnTo || '/dashboard');
    
    if (error) {
      setError(getReadableErrorMessage(error));
      setGoogleLoading(false);
    }
    // Note: If successful, the page will redirect, so we don't need to handle success
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isLoading = loading || googleLoading;

  if (success) {
    return (
      <div className="min-h-screen bg-theme-bg bg-mesh flex flex-col items-center justify-center p-4">
        {/* Theme toggle in top-right corner */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <LanguageSelector />
          <ThemeToggle />
        </div>

        <div className="card p-8 max-w-md w-full text-center animate-slide-up">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-theme-success-bg mb-6">
            <CheckCircle className="h-8 w-8 text-theme-success" />
          </div>
          <h2 className="font-display text-2xl font-bold text-theme-text mb-2">
            {tt('Account created!')}
          </h2>
          <p className="text-theme-text-secondary mb-4">
            {tt('Please check your email to verify your account.')}
          </p>
          <p className="text-sm text-theme-text-muted">
            {tt('Redirecting to login...')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme-bg bg-mesh flex flex-col items-center justify-center p-4">
      {/* Theme toggle in top-right corner */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <LanguageSelector />
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <img 
            src="/logo.png" 
            alt="Llambada" 
            className="h-16 w-16 mx-auto rounded-2xl shadow-xl shadow-accent-500/25 mb-6"
          />
          <h1 className="font-display text-3xl font-bold text-theme-text mb-2">
            {tt('Create an account')}
          </h1>
          <p className="text-theme-text-secondary">
            {tt('Start building your LLM projects today')}
          </p>
        </div>

        <div className="card p-8 animate-slide-up">
          {error && (
            <div className="flex items-center gap-3 p-4 rounded-lg bg-theme-error-bg border border-theme-error-border text-theme-error mb-5">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <div className="text-sm">
                <p>{tt(error)}</p>
                {error.includes('already exists') && (
                  <p className="mt-2">
                    <Link to="/login" className="font-medium underline hover:no-underline">
                      {tt('Go to Sign In')}
                    </Link>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Google Sign-up Button */}
          <button
            type="button"
            onClick={handleGoogleSignup}
            disabled={isLoading}
            className="btn btn-secondary w-full py-3 mb-4 flex items-center justify-center gap-2"
          >
            {googleLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <GoogleIcon className="h-5 w-5" />
            )}
            {googleLoading ? tt('Connecting to Google...') : tt('Sign up with Google')}
          </button>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-theme-border-muted"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-theme-surface text-theme-text-muted">{tt('Or sign up with email')}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-theme-text-secondary mb-2">
                {tt('Display name')}
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-text-muted" />
                <input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="input pl-11"
                  placeholder={tt('John Doe')}
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="country" className="block text-sm font-medium text-theme-text-secondary mb-2">
                {tt('Country')} <span className="text-theme-error">*</span>
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-text-muted" />
                <select
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="input pl-11 appearance-none"
                  required
                  disabled={isLoading}
                >
                  <option value="">{tt('Select your country')}</option>
                  {STRIPE_COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <p className="mt-1.5 text-xs text-theme-text-muted">
                {tt('Used for payouts. Cannot be changed later.')}
              </p>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-theme-text-secondary mb-2">
                {tt('Email address')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-text-muted" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-11"
                  placeholder={tt('you@example.com')}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-theme-text-secondary mb-2">
                {tt('Password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-theme-text-muted" />
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-11"
                  placeholder="••••••••"
                  required
                  minLength={6}
                  disabled={isLoading}
                />
              </div>
              <p className="mt-1.5 text-xs text-theme-text-muted">
                {tt('Must be at least 6 characters')}
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full py-3"
            >
              {loading ? (
                <LoadingSpinner size="sm" />
              ) : (
                tt('Create account')
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-theme-text-secondary">
              {tt('Already have an account?')}{' '}
              <Link to={returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login'} className="link">
                {tt('Sign in')}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* In-App Browser Warning Modal */}
      <Modal
        isOpen={showInAppWarning}
        onClose={() => setShowInAppWarning(false)}
        title={tt('Browser Not Supported')}
        size="sm"
      >
        <div className="flex flex-col gap-4 text-center">
          <div className="bg-amber-500/10 text-amber-600 dark:text-amber-400 p-4 rounded-lg flex flex-col items-center gap-2">
            <AlertTriangle className="h-8 w-8" />
            <p className="font-medium">{tt("Google Sign-up is blocked by this app's built-in browser.")}</p>
          </div>
          
          <div className="text-theme-text-secondary text-sm space-y-2">
            <p>{tt("To sign up with Google, you must open this page in your phone's main browser (Safari or Chrome).")}</p>
            <p className="font-semibold text-theme-text">{tt('Tap the menu icon (•••) in the corner and select "Open in Browser".')}</p>
          </div>

          <div className="relative my-2">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-theme-border-muted"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-theme-surface text-theme-text-muted">{tt('Or copy link manually')}</span>
            </div>
          </div>

          <button
            onClick={handleCopyLink}
            className="btn btn-secondary w-full flex items-center justify-center gap-2"
          >
            {copied ? (
              <>
                {tt('Copied!')} <CheckCircle className="h-4 w-4 text-theme-success" />
              </>
            ) : (
              <>
                {tt('Copy Page Link')} <Copy className="h-4 w-4" />
              </>
            )}
          </button>

          <button
            onClick={() => setShowInAppWarning(false)}
            className="btn btn-primary w-full"
          >
            {tt('Got it')}
          </button>
        </div>
      </Modal>
    </div>
  );
}
